/**
 * /showme API client.
 *
 * Session token (24h) lives in localStorage. /ask + /sources require the
 * X-Showme-Token header.
 */
const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'showme:session';
const NAME_KEY = 'showme:user_name';

export function readStoredName() {
  try {
    return localStorage.getItem(NAME_KEY) || '';
  } catch {
    return '';
  }
}

export function storeName(name) {
  if (name && name.trim()) localStorage.setItem(NAME_KEY, name.trim().slice(0, 60));
}

export function clearName() {
  localStorage.removeItem(NAME_KEY);
}

export function readStoredSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.token || !parsed?.expires_at) return null;
    if (parsed.expires_at * 1000 <= Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function storeSession(session) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearSession() {
  localStorage.removeItem(STORAGE_KEY);
}

async function postJson(path, body, token) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['X-Showme-Token'] = token;
  const name = readStoredName();
  if (name) headers['X-Showme-User'] = name;

  const url = `${API}${path}`;
  const init = {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  };

  // The backend itself retries Azure (4 attempts w/ backoff). On top of that
  // we add ONE more shot here for pod/ingress cold-starts on the preview env
  // — those typically present as a network drop or a 502/504 before the
  // backend even gets the request. We back off ~1.5s then try again.
  const transientStatuses = new Set([0, 408, 425, 429, 502, 503, 504]);
  const maxAttempts = 2;
  let lastErr;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const res = await fetch(url, init);
      const data = await res.json().catch(() => ({}));
      if (!res.ok && transientStatuses.has(res.status) && attempt < maxAttempts) {
        await new Promise((r) => setTimeout(r, 1500));
        continue;
      }
      return { ok: res.ok, status: res.status, data };
    } catch (err) {
      lastErr = err;
      if (attempt < maxAttempts) {
        await new Promise((r) => setTimeout(r, 1500));
        continue;
      }
      throw lastErr;
    }
  }
  throw lastErr;
}

async function getJson(path, token) {
  const headers = token ? { 'X-Showme-Token': token } : {};
  const name = readStoredName();
  if (name) headers['X-Showme-User'] = name;
  const res = await fetch(`${API}${path}`, { headers });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

export async function unlock(code) {
  const { ok, status, data } = await postJson('/api/showme/unlock', { code });
  if (ok && data?.token) {
    storeSession({ token: data.token, expires_at: data.expires_at });
  }
  return { ok, status, data };
}

export async function ask(question, history, token) {
  return postJson('/api/showme/ask', { question, history }, token);
}

export async function draftKit(goal, uiNotes, images, title, token, textAssets) {
  return postJson(
    '/api/showme/kit/draft',
    {
      goal,
      ui_notes: uiNotes || '',
      images: images || [],
      title: title || '',
      text_assets: textAssets || [],
    },
    token,
  );
}

export async function listSources(token) {
  return getJson('/api/showme/sources', token);
}

// ── Streaming (NDJSON) variants ───────────────────────────────────────────
// Keeps bytes flowing so the edge proxy never idle-times-out on slow/vision
// drafts. Parses one JSON object per line: {type:delta|done|error}.
async function postStream(path, body, token, onDelta) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['X-Showme-Token'] = token;
  const name = readStoredName();
  if (name) headers['X-Showme-User'] = name;

  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  // Pre-stream failures (401/400/413/…) arrive as a normal JSON response.
  if (!res.ok || !res.body) {
    const data = await res.json().catch(() => ({}));
    return {
      ok: false,
      status: res.status,
      citations: [],
      usage: null,
      error: (typeof data?.detail === 'string' ? data.detail : null) || `Request failed (HTTP ${res.status})`,
    };
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  const result = { ok: true, status: 200, citations: [], usage: null, error: null };

  const handleLine = (line) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    let ev;
    try { ev = JSON.parse(trimmed); } catch { return; }
    if (ev.type === 'delta') {
      if (onDelta) onDelta(ev.text || '');
    } else if (ev.type === 'done') {
      result.citations = ev.citations || [];
      result.usage = ev.usage || null;
    } else if (ev.type === 'error') {
      result.ok = false;
      result.error = ev.detail || 'Stream error';
      result.status = ev.status || 502;
    }
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf('\n')) >= 0) {
      handleLine(buf.slice(0, idx));
      buf = buf.slice(idx + 1);
    }
  }
  if (buf) handleLine(buf);
  return result;
}

export async function streamAsk(question, history, token, onDelta) {
  return postStream('/api/showme/ask/stream', { question, history }, token, onDelta);
}

export async function streamKitDraft(goal, uiNotes, images, title, textAssets, token, onDelta) {
  return postStream(
    '/api/showme/kit/draft/stream',
    { goal, ui_notes: uiNotes || '', images: images || [], title: title || '', text_assets: textAssets || [] },
    token,
    onDelta,
  );
}

export async function listLogs(token, limit = 50) {
  return getJson(`/api/showme/logs?limit=${limit}`, token);
}

export async function uploadOpenapi(file, token, adminPassword) {
  return uploadFile('/api/showme/sources/openapi', file, token, adminPassword);
}

export async function uploadMarkdown(file, token, adminPassword) {
  return uploadFile('/api/showme/sources/md', file, token, adminPassword);
}

export async function deleteMarkdown(filename, token, adminPassword) {
  const res = await fetch(`${API}/api/showme/sources/md/${encodeURIComponent(filename)}`, {
    method: 'DELETE',
    headers: {
      'X-Showme-Token': token,
      'X-Showme-Admin': adminPassword,
    },
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

async function uploadFile(path, file, token, adminPassword) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: {
      'X-Showme-Token': token,
      'X-Showme-Admin': adminPassword,
      ...(readStoredName() ? { 'X-Showme-User': readStoredName() } : {}),
    },
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}
